import { Component, EventEmitter, Output } from '@angular/core'
import { closeJobSelectionDialog } from '../../../../shared/utils/job-selection-dialog-utils'
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms'
import {
    ClusteringNormalization,
    PipelineType,
    UmapDistanceMetric,
    UmapParameters,
} from '../../../../shared/model/jobs'

@Component({
    selector: 'app-umap-clustering-selection',
    templateUrl: './umap-selection.component.html',
    styleUrls: ['./umap-selection.component.scss'],
})
export class UmapSelectionComponent {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)
    UmapDistanceMetricValues = Object.values(UmapDistanceMetric)

    readonly umapForm: FormGroup

    readonly NUMBER_OF_NEIGHBORS_FORM_KEY = 'numberOfNeighbors'
    readonly NUMBER_OF_EPOCHS_FORM_KEY = 'numberOfEpochs'
    readonly MINIMUM_DISTANCE_FORM_KEY = 'minimumDistance'
    readonly SPREAD_FORM_KEY = 'spread'
    readonly RANDOM_INITIALIZATION_FORM_KEY = 'randomInitialization'
    readonly DISTANCE_METRIC_FORM_KEY = 'distanceMetric'
    readonly NORMALIZATION_FORM_KEY = 'normalization'

    constructor(private readonly formBuilder: FormBuilder) {
        this.umapForm = this.formBuilder.group({
            [this.NUMBER_OF_NEIGHBORS_FORM_KEY]: new FormControl(15, [
                Validators.required,
                Validators.min(2),
            ]),
            [this.NUMBER_OF_EPOCHS_FORM_KEY]: new FormControl(200.0, [
                Validators.required,
                Validators.min(1),
            ]),
            [this.MINIMUM_DISTANCE_FORM_KEY]: new FormControl(0.1, [
                Validators.required,
                Validators.min(Number.EPSILON),
            ]),
            [this.SPREAD_FORM_KEY]: new FormControl(1.1, [
                Validators.required,
                Validators.min(Number.EPSILON),
            ]),
            [this.RANDOM_INITIALIZATION_FORM_KEY]: new FormControl(true, [Validators.required]),
            [this.DISTANCE_METRIC_FORM_KEY]: new FormControl(UmapDistanceMetric.Euclidean, [
                Validators.required,
            ]),
            [this.NORMALIZATION_FORM_KEY]: new FormControl(ClusteringNormalization.None, [
                Validators.required,
            ]),
        }, { validators: this.minimumDistanceLessThanOrEqualSpreadValidator })
    }

    @Output() paramsSet = new EventEmitter<UmapParameters>()

    get paramsValid(): boolean {
        return this.umapForm.valid
    }

    /** Validation messages shown above Next */
    get validationSummaryMessages(): string[] {
        const msgs: string[] = []
        const f = this.umapForm

        const neighborsCtl = f.get(this.NUMBER_OF_NEIGHBORS_FORM_KEY)
        if (neighborsCtl?.hasError('min')) {
            msgs.push('Number of neighbors must be at least 2.')
        }

        const epochsCtl = f.get(this.NUMBER_OF_EPOCHS_FORM_KEY)
        if (epochsCtl?.hasError('min')) {
            msgs.push('Number of epochs must be at least 1.')
        }

        const minDistCtl = f.get(this.MINIMUM_DISTANCE_FORM_KEY)
        if (minDistCtl?.hasError('min')) {
            msgs.push('Minimum distance must be greater than 0.')
        }

        const spreadCtl = f.get(this.SPREAD_FORM_KEY)
        if (spreadCtl?.hasError('min')) {
            msgs.push('Spread must be greater than 0.')
        }

        if (
            f.errors?.['minimumDistanceExceedsSpread'] &&
            (f.get(this.MINIMUM_DISTANCE_FORM_KEY)?.dirty ||
                f.get(this.SPREAD_FORM_KEY)?.dirty)
        ) {
            msgs.push('Minimum distance must be less than or equal to spread.')
        }

        return msgs
    }

    cancel(): void {
        closeJobSelectionDialog()
    }

    confirmParams(): void {
        if (!this.paramsValid) return

        this.paramsSet.emit({ type: PipelineType.umapClustering, ...this.umapForm.value })
    }

    private readonly minimumDistanceLessThanOrEqualSpreadValidator = (
        control: AbstractControl
    ): ValidationErrors | null => {
        const minimumDistance = control.get(this.MINIMUM_DISTANCE_FORM_KEY)?.value
        const spread = control.get(this.SPREAD_FORM_KEY)?.value

        if (minimumDistance === null || minimumDistance === undefined || spread === null || spread === undefined) {
            return null
        }

        return Number(minimumDistance) <= Number(spread)
            ? null
            : { minimumDistanceExceedsSpread: true }
    }
}
